import {Component, OnInit} from '@angular/core';
import {SessionStorageService} from '@services/session-storage.service';
import {storageKey} from '@constants/storage.constants';
import {AesService} from '@services/aes.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {BaseWebservices} from '@webservices/base.webservices';
import {Router} from '@angular/router';
import {AccountsWebservices} from '@webservices/accounts.webservices';

interface OtpState {
  sent: boolean;
  verified: boolean;
  loading: boolean;
  cooldown: number;
  timer?: any;
  errorMessage?: string;
}

@Component({
  selector: 'app-user-verification',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './user-verification.component.html',
  styleUrl: './user-verification.component.scss'
})
export class UserVerificationComponent implements OnInit {

  email!: string;
  mobile!: string;

  verifyForm: FormGroup;

  emailState: OtpState = this.getInitialState();
  mobileState: OtpState = this.getInitialState();
  continueError?: string;

  constructor(
    private fb: FormBuilder,
    private storage: SessionStorageService,
    private aes: AesService,
    private account: AccountsWebservices,
    private base: BaseWebservices,
    private router: Router,
  ) {
    this.verifyForm = this.fb.group({
      emailOtp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      mobileOtp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngOnInit(): void {
    const token: string | null = this.storage.get(storageKey.token)
    if (token) {
      const decrypt = this.aes.decrypt(token);

      this.email = decrypt.email;
      this.mobile = decrypt.mobile;
    } else {
      this.router.navigate(['not-found']);
    }
  }

  getInitialState(): OtpState {
    return {
      sent: false,
      verified: false,
      loading: false,
      cooldown: 0
    };
  }

  sendOtp(type: 'email' | 'mobile') {
    const state = this.getState(type);

    if (state.cooldown > 0) return;

    state.loading = true;
    state.errorMessage = undefined;
    // ✅ CALL VERIFY API HERE (important for production)

    if (type === 'email') {
      let payload = {
        otp_type: 'verify_user_otp' as const,
        email: this.email ?? undefined,
      };

      this.base.sendOtp(payload).subscribe({
        next: (res) => {
          state.loading = false;

          if (!res.error) {
            state.sent = true;
            this.startCooldown(state, 30);
          }
        },
        error: () => state.loading = false
      });
    } else {
      let payload = {
        otp_type: 'verify_user_otp' as const,
        mobile: this.mobile ? Number(this.mobile) : undefined
      };

      this.base.sendOtp(payload).subscribe({
        next: (res) => {
          state.loading = false;

          if (!res.error) {
            state.sent = true;
            this.startCooldown(state, 30);
          }
        },
        error: () => state.loading = false
      });
    }


  }

  verifyOtp(type: 'email' | 'mobile') {
    const state = this.getState(type);

    const control = type === 'email'
      ? this.verifyForm.get('emailOtp')
      : this.verifyForm.get('mobileOtp');

    if (control?.invalid) {
      control.markAllAsTouched();
      return;
    }

    // ✅ CALL VERIFY API HERE (important for production)
    state.errorMessage = undefined;

    let payload: any = {};
    if (type === 'email') {
      payload = {
        otp_type: 'verify_user_otp' as const,
        email: this.email ?? undefined,
        received_otp: control?.value,
      };
    } else {
      payload = {
        otp_type: 'verify_user_otp' as const,
        mobile: this.mobile ? Number(this.mobile) : undefined,
        received_otp: control?.value,
      };
    }

    this.base.verifyOtp(payload).subscribe({
      next: (res) => {
        if (!res.error) {
          state.verified = true;
          state.sent = false;
        }
      },
      error: (err) => {
        const body = err?.error;
        if (body?.message?.display && body?.message?.message) {
          state.errorMessage = body.message.message;
        } else {
          state.errorMessage = 'Verification failed. Please try again.';
        }
      }
    });
  }

  resetField(type: 'email' | 'mobile') {
    const state = this.getState(type);
    Object.assign(state, this.getInitialState());

    const control = type === 'email'
      ? this.verifyForm.get('emailOtp')
      : this.verifyForm.get('mobileOtp');

    control?.reset();
  }

  getState(type: 'email' | 'mobile'): OtpState {
    return type === 'email' ? this.emailState : this.mobileState;
  }

  startCooldown(state: OtpState, seconds: number) {
    state.cooldown = seconds;

    state.timer = setInterval(() => {
      state.cooldown--;

      if (state.cooldown <= 0) {
        clearInterval(state.timer);
      }
    }, 1000);
  }

  continue() {
    this.continueError = undefined;
    const payload = {
      email_otp: this.verifyForm.get('emailOtp')?.value,
      mobile_otp: this.verifyForm.get('mobileOtp')?.value,
      token: this.storage.get(storageKey.token) ?? undefined,
    }
    this.account.verifyUser(payload).subscribe({
      next: (res) => {
        if (!res.error) {
          this.router.navigate(['/verification-complete']);
        }
      },
      error: (err) => {
        const body = err?.error;
        if (body?.message?.display && body?.message?.message) {
          this.continueError = body.message.message;
        } else {
          this.continueError = 'Something went wrong. Please try again.';
        }
      }
    });
  }

}
